import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery } from "@tanstack/react-query";
import { formatDateTimeSr } from "@normax-performance/shared-format";
import type { DailyEntryHistoryFieldChangeDto } from "@normax-performance/shared-types";
import { dailyEntryApi } from "../../../lib/api-client/endpoints";
import { Badge, Modal } from "../../../shared-ui/primitives";
import {
  formatDurationMinutesValue,
  formatPerformanceRatioValue,
  formatPlannedQuantityValue,
  formatReportedQuantityValue,
} from "./dailyEntryHistoryFormat";
import "./DailyEntryHistoryModal.css";

/**
 * Read-only "Istorija izmena" (correction/audit history) — no edit/delete
 * controls anywhere in here, matching the read-only contract of
 * `GET /daily-entries/:id/history` (docs/NORMAX_PERFORMANCE_DAILY_ENTRY_AUDIT_HISTORY_IMPLEMENTATION_REPORT.md).
 * Available to both ADMIN and DIRECTOR (server-enforced by the route's
 * `@Roles`, not just this UI). Business-readable value formatting lives in
 * `dailyEntryHistoryFormat.ts` (Daily Entry Audit History UI Polish —
 * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_AUDIT_HISTORY_UI_POLISH_REPORT.md).
 */
export interface DailyEntryHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dailyEntryId: string | null;
}

/** Must match `AuditService.findForDailyEntry`'s default server-side `limit` — used only to decide whether to show the "showing last N" notice, never to truncate client-side. */
const HISTORY_SERVER_LIMIT = 100;

function fieldLabel(field: string, t: TFunction<"dailyEntry">): string {
  return t(`history.fields.${field}` as never, { defaultValue: field });
}

/**
 * Formats one side of a field change for display — never a raw UUID (the
 * backend already resolved those to names or `null`) and never raw,
 * unrounded decimal.js precision (the exact reported UI Polish complaint —
 * see `dailyEntryHistoryFormat.ts` for why `plannedQuantityRaw`/
 * `performanceRatioRaw` need real rounding, not a string `.replace()`).
 */
function formatChangeValue(field: string, value: string | null, t: TFunction<"dailyEntry">): string {
  if (value === null) return "—";
  if (field === "status") return t(`status.${value}` as never, { defaultValue: value });
  if (field === "entryType") return t(`history.entryTypeValues.${value}` as never, { defaultValue: value });
  if (field === "durationMinutes") return `${formatDurationMinutesValue(value)} min`;
  if (field === "plannedQuantityRaw") return formatPlannedQuantityValue(value);
  if (field === "performanceRatioRaw") return formatPerformanceRatioValue(value);
  if (field === "reportedQuantity") return formatReportedQuantityValue(value);
  // productId/operationId/neutralActivityId — already a resolved catalog
  // name (or null, handled above) from the backend; nothing left to do.
  return value;
}

function ChangeLine({ change, t }: { change: DailyEntryHistoryFieldChangeDto; t: TFunction<"dailyEntry"> }) {
  const oldStr = formatChangeValue(change.field, change.oldValue, t);
  const newStr = formatChangeValue(change.field, change.newValue, t);
  return (
    <li>
      {fieldLabel(change.field, t)}: {oldStr} → {newStr}
    </li>
  );
}

export function DailyEntryHistoryModal({ open, onOpenChange, dailyEntryId }: DailyEntryHistoryModalProps) {
  const { t } = useTranslation("dailyEntry");

  const historyQuery = useQuery({
    queryKey: ["daily-entry-history", dailyEntryId],
    queryFn: () => dailyEntryApi.history(dailyEntryId!),
    enabled: open && !!dailyEntryId,
  });

  const items = historyQuery.data ?? [];

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("history.modalTitle")}
      size="lg"
      showCloseButton
      closeButtonLabel={t("history.closeAriaLabel")}
    >
      <div className="nx-history">
        {historyQuery.isLoading ? (
          <p className="nx-history__hint">…</p>
        ) : historyQuery.isError ? (
          <p className="nx-history__hint" role="alert">
            {t("history.loadError")}
          </p>
        ) : items.length === 0 ? (
          <p className="nx-history__hint">{t("history.empty")}</p>
        ) : (
          <>
            <p className="nx-history__notice">{t("history.currentNameNotice")}</p>
            {items.length >= HISTORY_SERVER_LIMIT ? (
              <p className="nx-history__notice">{t("history.limitNotice", { count: HISTORY_SERVER_LIMIT })}</p>
            ) : null}
            <ul className="nx-history__list">
              {items.map((item) => (
                <li key={item.id} className="nx-history__item">
                  <div className="nx-history__item-header">
                    <span className="nx-history__timestamp">{formatDateTimeSr(item.changedAt)}</span>
                    <Badge tone="neutral">{t(`history.actionLabels.${item.action}` as never)}</Badge>
                  </div>
                  {item.changedBy ? (
                    <div className="nx-history__actor">
                      {item.changedBy.email} · {item.changedBy.role}
                    </div>
                  ) : null}
                  {item.reason ? (
                    <div className="nx-history__reason">
                      <strong>{t("history.reasonLabel")}:</strong> {item.reason}
                    </div>
                  ) : null}
                  {item.fieldChanges.length > 0 ? (
                    <div className="nx-history__changes">
                      <div className="nx-history__changes-label">{t("history.changesLabel")}:</div>
                      <ul>
                        {item.fieldChanges.map((change) => (
                          <ChangeLine key={change.field} change={change} t={t} />
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  );
}
